/**
 * Expand outliner "group" folders into scene object ids that can receive library / rule materials.
 */
export interface GroupMaterialSceneObject {
  id: string;
  type?: string;
  parentId?: string | null;
  metadata?: { parentId?: string | null };
  assetId?: string | null;
  asset?: { url?: string } | null;
}

function parentIdOf(o: GroupMaterialSceneObject): string | null {
  const fromRoot = o.parentId ?? null;
  if (fromRoot) return fromRoot;
  const m = o.metadata?.parentId;
  return m ?? null;
}

function canReceiveLibraryMaterial(o: GroupMaterialSceneObject): boolean {
  if (o.type === 'primitive') return true;
  if (o.type === 'mesh') {
    const url = o.asset?.url;
    if (typeof url === 'string' && url.length > 0) return true;
    if (o.assetId) return true;
  }
  return false;
}

/** BFS descendants under `groupObjectId` (not including the group itself). */
export function collectGroupMaterialReceiverIds(
  objects: GroupMaterialSceneObject[],
  groupObjectId: string,
): string[] {
  const byParent = new Map<string | null, string[]>();
  for (const o of objects) {
    const pid = parentIdOf(o);
    const list = byParent.get(pid) ?? [];
    list.push(o.id);
    byParent.set(pid, list);
  }
  const byId = new Map(objects.map((o) => [o.id, o]));
  const out: string[] = [];
  const queue = [...(byParent.get(groupObjectId) ?? [])];
  const seen = new Set<string>();
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const o = byId.get(id);
    if (!o) continue;
    if (canReceiveLibraryMaterial(o)) out.push(id);
    for (const c of byParent.get(id) ?? []) queue.push(c);
  }
  return out;
}
